import React from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useIdeas } from '../../hooks/useIdeas';
import IdeaFilters from './IdeaFilters';
import IdeasList from './IdeasList';
import OnboardingModal from '../OnboardingModal';
import TrialStatusModal from '../TrialStatusModal';
import SubscriptionExpiredModal from '../SubscriptionExpiredModal';
import SubscriptionCancellationBanner from '../SubscriptionCancellationBanner';
import ResourceLimitCard from '../common/ResourceLimitCard';
import CollapsibleInfoCard from '../common/CollapsibleInfoCard';
import { useCreateWithTrialCheck } from '../../hooks/useCreateWithTrialCheck';

const IdeasContainer: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  
  const {
    // Data
    ideas,
    loading,
    error,
    totalCount,
    
    // Pagination
    page,
    rowsPerPage,
    
    // Sorting
    sortConfig,
    
    // Filtering
    filterConfig,
    showFilters,
    availablePlatforms,
    availableSectors,
    availableImpactLevels,
    
    // Organization info
    orgInfo,
    showOnboarding,
    
    // Trial/Subscription modals
    isTrialExpiredModalOpen: isTrialStatusModalOpen,
    isTrialAdmin,
    closeTrialExpiredModal: closeTrialStatusModal,
    isSubscriptionExpiredModalOpen,
    isSubscriptionAdmin,
    closeSubscriptionExpiredModal,
    
    // Actions
    setShowFilters,
    handleSort,
    handleFilterChange,
    clearFilters,
    fetchIdeas,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useIdeas();

  const {
    handleCreateAction,
    isTrialStatusModalOpen: createTrialStatusModalOpen,
    closeTrialStatusModal: closeCreateTrialStatusModal,
    isAdmin: createTrialAdmin
  } = useCreateWithTrialCheck();
  
  const handleNewIdea = () => {
    handleCreateAction(() => navigate('/ideas/create'));
  };

  if (loading && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If onboarding is required, show the modal and a minimal view
  if (showOnboarding) {
    return <OnboardingModal isOpen={showOnboarding} onClose={() => {}} />;
  }

  // If there's an error and no modal is active, show error message
  if (error && !isTrialStatusModalOpen && !isSubscriptionExpiredModalOpen) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => fetchIdeas()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <>
      {orgInfo?.status === 'ACTIVE_UNTIL' && (
         <SubscriptionCancellationBanner 
            isAdmin={user?.role === 'ADMIN'} 
            expiryDate={orgInfo.nextBillingAt} 
         />
      )}

      <TrialStatusModal 
        isOpen={isTrialStatusModalOpen} 
        onClose={closeTrialStatusModal} 
        isAdmin={isTrialAdmin}
        status="expired"
      />
      <SubscriptionExpiredModal 
        isOpen={isSubscriptionExpiredModalOpen} 
        onClose={closeSubscriptionExpiredModal} 
        isAdmin={isSubscriptionAdmin} 
      />
      
      {/* Trial expired modal for create button clicks */}
      <TrialStatusModal 
        isOpen={createTrialStatusModalOpen} 
        onClose={closeCreateTrialStatusModal} 
        isAdmin={createTrialAdmin}
        status="expired"
      />

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 4, bgcolor: 'background.paper' }}>
        {/* Responsive Header */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: 3,
          gap: { xs: 2, sm: 0 }
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1" 
            gutterBottom={!isMobile}
            sx={{ mb: { xs: 0, sm: 1 } }}
          >
            Ideas
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNewIdea}
            fullWidth={isMobile}
            sx={{ height: 'fit-content' }}
          >
            New Idea
          </Button>
        </Box>

        {/* Info */}
        <CollapsibleInfoCard
          title="About Ideas"
          content="Ideas are AI-generated post suggestions tuned to your Personas and Social Accounts. Generated ideas are run through our simulation engine to validate impact."
        />

        {/* Quota card: simulations & ideas share the same monthly quota */}
        {orgInfo && (
          <ResourceLimitCard
            icon={<AssessmentIcon sx={{ color: 'primary.main' }} />}
            title="Simulations & Ideas"
            used={orgInfo.simUsed ?? 0}
            limit={orgInfo.plan === 'INTERNAL' ? null : (orgInfo.monthlySimQuota ?? null)}
            resetAt={orgInfo.plan === 'INTERNAL' ? null : (orgInfo.quotaResetAt ?? null)}
            isAdmin={user?.role === 'ADMIN'}
            sx={{ mb: 2 }}
          />
        )}

        {/* Filter Controls */}
        <IdeaFilters
          filterConfig={filterConfig}
          showFilters={showFilters}
          availablePlatforms={availablePlatforms}
          availableSectors={availableSectors}
          availableImpactLevels={availableImpactLevels}
          onFilterChange={handleFilterChange}
          onToggleFilters={() => setShowFilters(!showFilters)}
          onClearFilters={clearFilters}
        />

        {/* Ideas List */}
        <IdeasList
          ideas={ideas}
          sortConfig={sortConfig}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onNewIdea={handleNewIdea}
        />
      </Paper>
    </>
  );
};

export default IdeasContainer;